import { memo, type ReactNode } from 'react';

import { type WorkspaceAgentAskAnswer } from '~/components/v2/components/workspace/workspace-agent-panel/utils/parseWorkspaceAgentAskAnswer';

const DISMISSED_LABEL = 'Dismissed';
const LABEL_SEPARATOR = ', ';
const AGENT_SPEAKER_LABEL = 'Agent asked: ';
const USER_SPEAKER_LABEL = 'You said: ';

export type WorkspaceAgentPanelElicitationAnswerProps = {
  answer: WorkspaceAgentAskAnswer;
  question: ReactNode;
};

/** A dismissed or expired ask got nothing from the user, so a status line stands in place of the answer. */
const isWorkspaceAgentAskAnswerStatus = (
  answer: WorkspaceAgentAskAnswer,
): answer is Extract<WorkspaceAgentAskAnswer, { kind: 'dismissed' | 'unanswered' }> =>
  answer.kind === 'dismissed' || answer.kind === 'unanswered';

const getAnswerLines = (answer: WorkspaceAgentAskAnswer): string[] => {
  switch (answer.kind) {
    case 'selected':
      return [
        ...(answer.labels.length > 0 ? [answer.labels.join(LABEL_SEPARATOR)] : []),
        ...(answer.customText ? [answer.customText] : []),
      ];
    case 'text':
      return [answer.text];
    case 'fields':
      return answer.fields.map((field) => `${field.label}: ${field.value}`);
    case 'number':
      return [String(answer.value)];
    case 'dismissed':
    case 'unanswered':
      return [];
  }
};

/**
 * One card holding an ask and the user's answer to it. The ask dock closes once it is answered, so
 * this card is the thread's record of both what the agent asked and what the user chose. A dock
 * that was dismissed or expired keeps the same card with a quiet status line in place of the
 * answer, since the user said nothing.
 */
export const WorkspaceAgentPanelElicitationAnswer = memo(
  ({ answer, question }: WorkspaceAgentPanelElicitationAnswerProps) => {
    return (
      <div
        className="flex w-full flex-col gap-1 rounded-12 ads:bg-raised bg-gray-2 p-3"
        data-testid="WORKSPACE_AGENT_ELICITATION_ANSWER"
      >
        {question == null ? null : (
          <p
            className="break-words font-semibold ads:text-primary text-12 text-gray-12 leading-4"
            data-testid="WORKSPACE_AGENT_ELICITATION_QUESTION"
          >
            <span className="sr-only">{AGENT_SPEAKER_LABEL}</span>
            {question}
          </p>
        )}
        {isWorkspaceAgentAskAnswerStatus(answer) ? (
          <span
            className="ads:text-metadata text-12 text-gray-10 leading-4"
            data-testid="WORKSPACE_AGENT_ELICITATION_ANSWER_STATUS"
          >
            {answer.kind === 'dismissed' ? DISMISSED_LABEL : answer.reason}
          </span>
        ) : (
          <div className="whitespace-pre-wrap break-words ads:text-secondary text-12 text-gray-11 leading-4">
            <span className="sr-only">{USER_SPEAKER_LABEL}</span>
            {getAnswerLines(answer).map((line, index) => (
              <div data-testid="WORKSPACE_AGENT_ELICITATION_ANSWER_LINE" key={`${index}-${line}`}>
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

WorkspaceAgentPanelElicitationAnswer.displayName = 'WorkspaceAgentPanelElicitationAnswer';
